/**
 * What a limited admin is allowed to do.
 *
 * A permission is either a whole menu (`draws`) or one action inside it
 * (`draws:settle`). Holding the menu implies every action under it, which is what makes
 * this safe to introduce: an account that was granted `draws` before these existed keeps
 * working exactly as it did, and nobody has to migrate a permissions array.
 *
 * The tree lives here rather than in either app because both sides must agree — the API
 * decides whether a call is allowed, and the console decides whether to draw the button.
 * A button that 403s on click is a worse experience than one that was never shown, and
 * two hand-maintained lists would drift apart the first time an endpoint moved.
 */
export const PERMISSION_MENUS = [
    {
        key: 'draws',
        labelTh: 'หวย',
        actions: [
            { key: 'draws:view', labelTh: 'ดูข้อมูลหวย', descTh: 'เปิดเมนูหวย ดูงวดและผล — แก้อะไรไม่ได้', risk: 'read' },
            { key: 'draws:result', labelTh: 'ลงผลหวย', descTh: 'บันทึกผลและแก้ผลก่อนจ่ายเงิน', risk: 'money' },
            { key: 'draws:settle', labelTh: 'จ่ายเงิน (คำนวณผล)', descTh: 'สั่งจ่ายเงินรางวัลเข้าบัญชีสมาชิก', risk: 'money' },
            { key: 'draws:void', labelTh: 'ยกเลิกการจ่ายเงิน', descTh: 'ดึงเงินรางวัลที่จ่ายไปแล้วกลับคืน', risk: 'money' },
            { key: 'draws:cancel', labelTh: 'ยกเลิกงวด + คืนเงิน', descTh: 'ยกเลิกทั้งงวดและคืนเงินทุกบิล', risk: 'money' },
            { key: 'draws:open', labelTh: 'เปิดงวด / ปิดรับแทง', descTh: 'เปิดงวดใหม่ และสั่งปิดรับแทงก่อนเวลา', risk: 'config' },
            { key: 'draws:config', labelTh: 'ตั้งค่าหวย / เปิด-ปิดตลาด', descTh: 'ตารางออกผล เวลาปิดรับ เปิด-ปิดหวยและตลาด', risk: 'config' },
        ],
    },
    {
        key: 'payout-rates',
        labelTh: 'อัตราจ่าย',
        actions: [
            { key: 'payout-rates:view', labelTh: 'ดูอัตราจ่าย', descTh: 'ดูอัตราจ่ายและวงเงินของแต่ละหวย', risk: 'read' },
            { key: 'payout-rates:edit', labelTh: 'แก้อัตราจ่าย', descTh: 'เปลี่ยนอัตราจ่ายและวงเงิน — กระทบเงินที่ต้องจ่ายทุกบิล', risk: 'money' },
        ],
    },
    {
        key: 'number-limits',
        labelTh: 'ปิดรับเลข',
        actions: [
            { key: 'number-limits:view', labelTh: 'ดูยอดแทงรายเลข', descTh: 'ดูว่าเลขไหนมีคนแทงไปเท่าไหร่', risk: 'read' },
            { key: 'number-limits:edit', labelTh: 'ปิดรับเลข / ตั้งวงเงิน', descTh: 'สั่งปิดรับเลข และตั้งวงเงินต่อเลข', risk: 'config' },
        ],
    },
    {
        key: 'masters',
        labelTh: 'มาสเตอร์',
        actions: [
            { key: 'masters:view', labelTh: 'ดูสายงาน', descTh: 'ดูมาสเตอร์ เอเจนต์ และสมาชิกในสาย', risk: 'read' },
            { key: 'masters:manage', labelTh: 'สร้าง/แก้มาสเตอร์', descTh: 'สร้างมาสเตอร์ ตั้งเพดานหุ้น ระงับการใช้งาน', risk: 'money' },
            { key: 'masters:staging', labelTh: 'ตั้งสายทดสอบ', descTh: 'เปิด-ปิดสาย staging — เปิดให้ยิงคำสั่งขยับเงินจริงฝั่งเอเจนต์', risk: 'money' },
        ],
    },
    {
        key: 'staging',
        labelTh: 'ทดสอบเชื่อมต่อ',
        actions: [
            { key: 'staging:view', labelTh: 'ดูผลทดสอบ', descTh: 'ดูประวัติและรายงานการทดสอบ', risk: 'read' },
            { key: 'staging:run', labelTh: 'ยิงทดสอบ', descTh: 'ยิงคำสั่งไปหาเอเจนต์ — หัก/จ่ายเงินจริงในระบบของเขา', risk: 'money' },
        ],
    },
    {
        key: 'reports',
        labelTh: 'รายงาน',
        actions: [
            { key: 'reports:view', labelTh: 'ดูรายงาน', descTh: 'ดูรายงานได้-เสียทุกระดับ', risk: 'read' },
            { key: 'reports:export', labelTh: 'ส่งออก CSV', descTh: 'ดาวน์โหลดข้อมูลออกนอกระบบ', risk: 'config' },
        ],
    },
    { key: 'rounds', labelTh: 'รอบ', actions: [] },
    { key: 'bets', labelTh: 'การเดิมพัน', actions: [] },
    { key: 'games', labelTh: 'เกม', actions: [] },
    { key: 'settlements', labelTh: 'การชำระ', actions: [] },
    {
        key: 'partner-wallet',
        labelTh: 'เงินค้างกับ partner',
        actions: [
            { key: 'partner-wallet:view', labelTh: 'ดูรายการค้าง', descTh: 'ดูรายการจ่ายรางวัล/คืนเงินที่ส่งให้ partner ไม่สำเร็จหรือกำลังลองใหม่', risk: 'read' },
            { key: 'partner-wallet:retry', labelTh: 'ส่งใหม่ / ปิดเรื่อง', descTh: 'สั่งส่งรายการที่ค้างให้ partner อีกครั้ง หรือปิดเรื่องที่แก้กับ partner แล้ว — กระทบเงินของสมาชิก', risk: 'money' },
        ],
    },
    {
        key: 'keno-limits',
        labelTh: 'วงเงิน KENO',
        actions: [
            { key: 'keno-limits:view', labelTh: 'ดูวงเงิน KENO', descTh: 'ดูแถววงเงินแทง KENO และที่ master/agent ใช้ — แก้ไม่ได้', risk: 'read' },
            { key: 'keno-limits:edit', labelTh: 'แก้วงเงิน KENO', descTh: 'เพิ่ม แก้ ระงับแถววงเงิน และกำหนดว่า master/agent ใช้แถวไหน — กระทบสิ่งที่สมาชิกแทงได้ทันที', risk: 'config' },
        ],
    },
    { key: 'providers', labelTh: 'ผู้ให้ข้อมูล', actions: [] },
    { key: 'audit-logs', labelTh: 'บันทึกกิจกรรม', actions: [] },
    { key: 'settings', labelTh: 'การตั้งค่า', actions: [] },
];
/** Every key that may be granted — menus and their actions. */
export const ALL_PERMISSION_KEYS = PERMISSION_MENUS.flatMap((m) => [m.key, ...m.actions.map((a) => a.key)]);
const PERMISSION_KEY_SET = new Set(ALL_PERMISSION_KEYS);
/** Is this a key the system knows about? Guards what an API accepts into the column. */
export function isPermissionKey(key) {
    return PERMISSION_KEY_SET.has(key);
}
/**
 * Is this admin allowed to do `required`?
 *
 * `null` means super admin. Otherwise the exact key counts, and so does the menu it
 * belongs to — `draws` covers `draws:settle`, which is what keeps existing accounts
 * working and lets an operator grant a whole menu without ticking seven boxes.
 *
 * Deliberately one-directional: holding `draws:view` never implies `draws`.
 */
export function hasPermission(granted, required) {
    if (granted === null || granted === undefined)
        return true;
    if (granted.includes(required))
        return true;
    const colon = required.indexOf(':');
    return colon > 0 && granted.includes(required.slice(0, colon));
}
/** True when the admin can reach a menu at all — via the menu key or any action in it. */
export function canSeeMenu(granted, menuKey) {
    if (granted === null || granted === undefined)
        return true;
    return granted.some((g) => g === menuKey || g.startsWith(`${menuKey}:`));
}
//# sourceMappingURL=permissions.js.map